/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SubmissionStatus } from '../../models/SubmissionStatus';
import { Roles } from '../../shared/enums/Roles';
import useProcedure from '../../procedure/useProcedure';
import useSubmissionActions from '../../shared/authorization/useSubmissionActions';
import { useAuth } from '../../redux/features/auth/useAuth';

export default function useDraftFooter(onDialogClose, submissionId, submission) {
  const history = useHistory();
  const procedure = useProcedure();
  const { userData } = useAuth();
  const { t } = useTranslation();
  const { isLocked, canEdit, canSendToSign, canSendToFiling, canPay, canSendWithoutPaying } = useSubmissionActions();

  const [dialogStatus, setDialogStatus] = useState(false);
  const [revertStatusAction, setRevertStatusAction] = useState('');

  const isEditable = () => canEdit(submission);
  const isToSign = () => canSendToSign(submission);
  const isToSend = () => canSendToFiling(submission);
  const canPayButtonEnabled = () => canPay(submission);
  const canSendWithoutPayingButtonEnabled = () => canSendWithoutPaying(submission);

  const isExternalIntegrationPayment = () => {
    return submission?.fees?.methodOfPayment === 'payOnline' && !submission?.fees?.feesExtraData?.nonExternal;
  };

  function isBackToDraft() {
    if (submission) {
      if (submission.status === SubmissionStatus.READY_TO_SIGN.id) {
        return userData?.roles.includes(Roles.SUBMISSION_SIGNER);
      }
      if (submission.status === SubmissionStatus.READY_TO_SEND.id) {
        return userData?.roles.includes(Roles.SUBMISSION_SENDER) && !submission.paymentResponse?.successful;
      }
    }
    return false;
  }

  function isBackToSign() {
    if (submission && submission.status === SubmissionStatus.READY_TO_SEND.id) {
      return userData?.roles.includes(Roles.SUBMISSION_SENDER) && !submission.paymentResponse?.successful;
    }
    return false;
  }

  const handlePreviewClick = useCallback(() => {
    history.push(
      `/${procedure.id}/preview/${submissionId}`,
      [
        ...history?.location?.state,
        {
          goBackText: t(procedure.label),
          goBackPath: '/',
        },
      ].filter(Boolean),
    );
  }, [submissionId]);

  const handleEditSubmissionClick = useCallback(async () => {
    if (!isLocked(submission)) {
      history.push(`/${procedure.id}/draft/${submissionId}/edit`);
    }
  }, [submission, submissionId]);

  const closeDialogStatus = () => {
    onDialogClose();
    setDialogStatus(false);
    setRevertStatusAction('');
  };

  const handleBackDraftClick = useCallback(() => {
    setDialogStatus(true);
    setRevertStatusAction(SubmissionStatus.DRAFT.id);
  }, [submissionId]);

  const handleBackSignClick = useCallback(() => {
    setDialogStatus(true);
    setRevertStatusAction(SubmissionStatus.READY_TO_SIGN.id);
  }, [submissionId]);

  return {
    isEditable,
    isToSign,
    isBackToDraft,
    isBackToSign,
    isToSend,
    closeDialogStatus,
    isLocked,
    dialogStatus,
    revertStatusAction,
    handleEditSubmissionClick,
    handleBackDraftClick,
    handleBackSignClick,
    handlePreviewClick,
    procedure,
    canPayButtonEnabled,
    canSendWithoutPayingButtonEnabled,
    isExternalIntegrationPayment,
  };
}
